import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { usePieceTimeline } from "@/hooks/use-analytics";
import { useBrandContext } from "@/contexts/brand-context";
import { useLocale, formatNumber } from "@/i18n/format";
import { RetentionCurveChart } from "./retention-curve-chart";
import { sumReportedFollows, type PieceVideoStats } from "./video-stats";

interface PieceTimelineDrawerProps {
  brandId: string;
  pieceId: string | null;
  onClose: () => void;
}

interface TimelineRow {
  date: string;
  platform: string;
  impressions: number;
  reach: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  clicks?: number;
  views?: number;
  igReelsAvgWatchTimeMs?: string | null;
  igReelsVideoViewTotalTimeMs?: string | null;
  /** Null = not reported by the platform for this media. */
  follows?: string | null;
}

function VideoStatsBlock({ stats }: { stats: PieceVideoStats }) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  const dash = "—";
  const items: Array<[string, string]> = [
    [
      t("video.pctWatched"),
      stats.pctWatched != null
        ? `${formatNumber(stats.pctWatched, locale, { maximumFractionDigits: 1 })}%`
        : dash,
    ],
    [
      t("video.avgWatchTime"),
      stats.avgWatchTimeMs != null
        ? `${formatNumber(stats.avgWatchTimeMs / 1000, locale, { maximumFractionDigits: 1 })}s`
        : dash,
    ],
    [
      t("video.duration"),
      stats.durationSeconds != null
        ? `${formatNumber(stats.durationSeconds, locale, { maximumFractionDigits: 1 })}s${
            stats.durationSource === "asset" ? ` (${t("video.durationFromAsset")})` : ""
          }`
        : dash,
    ],
    [
      t("video.totalWatchTime"),
      stats.totalWatchTimeMs != null
        ? `${formatNumber(stats.totalWatchTimeMs / 60000, locale, { maximumFractionDigits: 1 })} min`
        : dash,
    ],
  ];
  if (stats.replays != null) {
    items.push([t("video.replays"), formatNumber(stats.replays, locale)]);
  }
  const points = stats.retentionCurve.map((p) => ({
    seconds: p.second,
    watchPct: p.ratio * 100,
  }));
  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid={`video-stats-${stats.platform}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("video.title", { platform: stats.platform.toLowerCase() })}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {items.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {points.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground">{t("video.retentionHint")}</div>
          <RetentionCurveChart points={points} emptyLabel={t("timeline.noData")} height={160} />
        </div>
      )}
    </div>
  );
}

export function PieceTimelineDrawer({
  brandId,
  pieceId,
  onClose,
}: PieceTimelineDrawerProps) {
  const { t } = useTranslation("analytics");
  const { brand } = useBrandContext();
  const { data, isLoading } = usePieceTimeline(brandId, pieceId);

  const locale = useLocale();
  const open = pieceId !== null;
  const rows: TimelineRow[] = data?.timeline ?? [];
  const videoStats: PieceVideoStats[] = data?.video ?? [];
  const follows = sumReportedFollows(rows);
  // Aggregate by date across platforms.
  const byDate = new Map<
    string,
    { date: string; impressions: number; reach: number; engagement: number }
  >();
  for (const r of rows) {
    const engagement =
      (r.likes ?? 0) + (r.comments ?? 0) + (r.shares ?? 0) + (r.saves ?? 0);
    const prev = byDate.get(r.date);
    if (prev) {
      prev.impressions += r.impressions;
      prev.reach += r.reach;
      prev.engagement += engagement;
    } else {
      byDate.set(r.date, {
        date: r.date,
        impressions: r.impressions,
        reach: r.reach,
        engagement,
      });
    }
  }
  const chartData = Array.from(byDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );


  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[540px] sm:max-w-[540px]">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>{t("timeline.title")}</SheetTitle>
            {pieceId && (
              <Link
                to={`/${brand.slug}/pieces/${pieceId}`}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {t("timeline.viewPiece")} <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </SheetHeader>
        <div className="mt-4">
          {isLoading ? (
            <div className="h-64 animate-pulse rounded-lg bg-muted" />
          ) : chartData.length === 0 ? (
            <div className="flex h-64 items-center justify-center rounded-lg border bg-muted/20 text-sm text-muted-foreground">
              {t("timeline.noData")}
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="impressions"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="reach"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="engagement"
                    stroke="#ec4899"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {rows.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="piece-follows">
              {t("timeline.follows")}:{" "}
              <span className="tabular-nums text-foreground">
                {follows != null ? formatNumber(follows, locale) : "—"}
              </span>
            </p>
          )}
          {videoStats.length > 0 && (
            <div className="mt-4 space-y-3">
              {videoStats.map((v) => (
                <VideoStatsBlock key={v.platform} stats={v} />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
